/**
 * MyFacilityReportsWidget — schlanker „Melden"-Zugang für normale Mitarbeiter.
 *
 * Wird nur angezeigt, wenn der Nutzer KEINEN Board-Zugriff hat (keine
 * facility-Berechtigung) UND Mitarbeiter-Meldungen firmenweit aktiviert sind.
 * Zeigt einen „Anliegen melden"-Button und die eigenen Einreichungen.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Wrench, Plus } from 'lucide-react';
import { facilityAPI, locationsAPI } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { ReportDialog } from '../facility/ReportDialog';
import {
  ORDER_TYPE_LABELS, STATUS_LABELS, STATUS_STYLES, fmtDateDe,
} from '../facility/constants';

export const MyFacilityReportsWidget = () => {
  const { hasPermission, isAdmin } = useAuth();
  const hasBoard = isAdmin || hasPermission('facility', 'view');
  const [enabled, setEnabled] = useState(false);
  const [ready, setReady] = useState(false);
  const [reports, setReports] = useState([]);
  const [locations, setLocations] = useState([]);
  const [open, setOpen] = useState(false);

  const loadReports = useCallback(() => {
    facilityAPI.myReports().then((r) => setReports(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (hasBoard) { setReady(true); return; }
    facilityAPI.selfMeta()
      .then((r) => {
        setEnabled(!!r.data?.reporting_enabled);
        if (r.data?.reporting_enabled) {
          loadReports();
          locationsAPI.getAll({ is_active: true }).then((l) => setLocations(l.data || [])).catch(() => {});
        }
      })
      .catch(() => {})
      .finally(() => setReady(true));
  }, [hasBoard, loadReports]);

  // Board-Nutzer und deaktiviertes Feature -> nichts anzeigen
  if (hasBoard || !ready || !enabled) return null;

  return (
    <Card className="border-border/60" data-testid="my-facility-reports-widget">
      <CardHeader className="border-b border-border/40 pb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <CardTitle className="font-heading text-lg flex flex-wrap items-center gap-2">
          <Wrench className="h-5 w-5 text-primary shrink-0" />
          Haustechnik-Meldung
        </CardTitle>
        <Button size="sm" className="rounded-full shrink-0 self-start sm:self-auto whitespace-nowrap" onClick={() => setOpen(true)} data-testid="my-facility-report-btn">
          <Plus className="h-4 w-4 mr-1" /> Anliegen melden
        </Button>
      </CardHeader>
      <CardContent className="p-4">
        <p className="text-xs text-muted-foreground mb-3">
          Schaden, Transport, Besorgung o. Ä. melden – die Haustechnik kümmert sich darum. Hier siehst du deine eigenen Meldungen.
        </p>
        {reports.length === 0 ? (
          <p className="text-sm text-muted-foreground py-2">Noch keine Meldungen abgegeben.</p>
        ) : (
          <ul className="space-y-1.5" data-testid="my-facility-reports-list">
            {reports.slice(0, 6).map((r) => (
              <li key={r.id} className="flex items-center justify-between gap-2 text-sm border-b border-border/40 pb-1.5">
                <div className="min-w-0">
                  <p className="font-medium truncate">{r.title}</p>
                  <p className="text-xs text-muted-foreground">{ORDER_TYPE_LABELS[r.order_type] || 'Auftrag'} · {fmtDateDe(r.created_at)}</p>
                </div>
                <Badge className={`text-[10px] shrink-0 ${STATUS_STYLES[r.status]}`}>{STATUS_LABELS[r.status]}</Badge>
              </li>
            ))}
          </ul>
        )}
      </CardContent>

      <ReportDialog
        open={open}
        onOpenChange={setOpen}
        mode="self"
        locations={locations}
        onCreated={loadReports}
      />
    </Card>
  );
};
